/**
 * TanStack Query Hook for Scaleup Jobs Impact KPI
 *
 * This hook provides access to scaleup jobs impact KPI data from the database.
 * NOTE: This KPI shows proportions/impact, NOT year-over-year trends.
 * Values represent % of jobs in scaleups vs total SME jobs.
 *
 * Database Source: web.v_kpi_scaleup_jobs_impact_v1 view
 *
 * @module useKpiScaleupJobsImpactV1
 * @category Database Hooks
 */

import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * Interface for Scaleup Jobs Impact KPI data
 */
interface KpiScaleupJobsImpact {
  scaleup_job_percentage: number;
  scaleup_company_percentage: number;
}

/**
 * Fetches scaleup jobs impact KPI data from the database
 *
 * @returns {Promise<KpiScaleupJobsImpact>} KPI data object with job and company percentages
 * @throws {Error} If the database query fails or returns invalid data
 * @internal
 */
const fetchKpiScaleupJobsImpact = async (): Promise<KpiScaleupJobsImpact> => {
  const { data, error } = await supabase
    .schema('web')
    .from('v_kpi_scaleup_jobs_impact_v1')
    .select('*')
    .single();

  if (error) {
    throw new Error(`Failed to fetch scaleup jobs impact KPI: ${error.message}`);
  }

  if (!data) {
    throw new Error('No data returned from scaleup jobs impact KPI query');
  }

  return data;
};

/**
 * React Hook for Scaleup Jobs Impact KPI Data
 *
 * Fetches and caches the scaleup jobs impact KPI data using TanStack Query.
 * Returns proportions: % of jobs in scaleups and % of scaleup companies.
 *
 * Query Configuration:
 * - Query Key: ['kpi', 'scaleup-jobs-impact', 'v1']
 * - Stale Time: 1 hour (3600000ms)
 * - Refetch on Window Focus: false
 * - Refetch on Mount: false
 * - Retry Attempts: 2
 *
 * @returns {UseQueryResult<KpiScaleupJobsImpact>} TanStack Query result object
 *
 * @example
 * ```typescript
 * const { data, isLoading, isError } = useKpiScaleupJobsImpactV1();
 *
 * if (isLoading) return <Spinner />;
 * if (isError) return <ErrorMessage />;
 *
 * return (
 *   <div>
 *     <p>{data.scaleup_job_percentage}% of all jobs</p>
 *     <p>{data.scaleup_company_percentage}% of companies</p>
 *   </div>
 * );
 * ```
 */
export const useKpiScaleupJobsImpactV1 = (): UseQueryResult<KpiScaleupJobsImpact> => {
  return useQuery<KpiScaleupJobsImpact>({
    queryKey: ['kpi', 'scaleup-jobs-impact', 'v1'],
    queryFn: fetchKpiScaleupJobsImpact,
    staleTime: 3600000, // 1 hour in milliseconds
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
  });
};
